

import { useState } from "react"
import {
  FileText,
  GraduationCap,
  Calendar,
  DollarSign,
  CheckCircle,
  AlertTriangle,
  Clock,
  BookOpen,
  User,
  MapPin,
  Globe,
  Phone,
  Download,
  Building,
  Shield,
  Star,
   
  Award, 
  Lightbulb
} from "lucide-react"

// quinquier list des document a avoir university
const universitySteps = [
  {
    step: 1,
    level: "DIPLOMA & BACHELOR",
    title: "Documents requis pour Diploma & Bachelor",
    description: "Documents de base nécessaires pour l'admission en Diploma ou Bachelor",
    documents: [
      "Photocopie du passeport",
      "Bulletin 6ème primaire",
      "Bulletin 5ème secondaire",
      "Bulletin 6ème secondaire",
      "Certificat de fin d'étude 6ème primaire",
      "Certificat de fin d'étude 6ème secondaire",
    ],
    icon: <GraduationCap className="h-6 w-6 text-cyan-600" />,
    color: "cyan",
    attention: [
      "Vos documents doivent être reconnus par l'école et l'état (bureau ministre de renseignement)",
      "Vos documents peuvent être traduits et certifiés dans votre pays d'origine, au ministère des affaires étrangères",
      "En cas de doutes ou retard vous pourrez le faire dans le pays à l'ambassade de votre pays d'origine (traduit et certifié), auprès d'un notaire ou un avocat (certifié), ou à l'alliance française (traduit)",
      "Si en cas de perte du bulletin d'un document, le certificat peut passer, mais tous ces documents vous seront obligatoirement nécessaires",
    ],
    conseils: [],
  },
  {
    step: 2,
    level: "MASTER DEGREE",
    title: "Documents requis pour Master Degree",
    description: "Documents nécessaires pour l'admission en Master, incluant le parcours complet",
    documents: [
      "Photocopie du passeport",
      "Bulletin 6ème secondaire",
      "Certificat de fin d'étude 6ème secondaire",
      "Diplôme de 1ère licence",
      "Diplôme de 2ème licence",
      "Diplôme de 3ème licence",
      "Certificat de fin d'études universitaires",
    ],
    icon: <Award className="h-6 w-6 text-blue-600" />,
    color: "blue",
    note: "Si vous avez été dans un système normal (donc ±5 ans), tous vos diplômes de licence seront exigés",
    attention: ["Mêmes exigences de reconnaissance et certification que pour le niveau Diploma & Bachelor"],
    conseils: [
      "Ayez une lettre de confirmation de la part de votre université confirmant votre parcours",
      "N'hésitez pas à vous munir d'une lettre de motivation et un CV",
      "Incluez certains de vos certificats de formations",
    ],
  },
  {
    step: 3,
    level: "PHD (DOCTORAT)",
    title: "Documents requis pour PhD (Doctorat)",
    description: "Documents complets nécessaires pour l'admission en Doctorat, parcours académique complet requis",
    documents: [
      "Photocopie du passeport",
      "Bulletin 6ème secondaire",
      "Certificat de fin d'études 6ème secondaire",
      "Diplôme de 1ère licence",
      "Diplôme de 2ème licence",
      "Diplôme de 3ème licence",
      "Certificat de fin d'études universitaires",
      "Diplôme de 1ère année de Master (±1 an)",
      "Diplôme de 2ème année de Master (±2 ans)",
    ],
    icon: <BookOpen className="h-6 w-6 text-purple-600" />,
    color: "purple",
    note: "Si vous avez été dans un système normal (donc ±5 ans), tous vos diplômes de licence seront exigés",
    attention: [
      "Vos documents doivent être reconnus par l'école et l'état (bureau ministre de renseignement)",
      "Vos documents peuvent être traduits et certifiés dans votre pays d'origine, au ministère des affaires étrangères",
      "En cas de doutes ou retard vous pourrez le faire dans le pays à l'ambassade de votre pays d'origine (traduit et certifié), auprès d'un notaire ou un avocat (certifié), ou à l'alliance française (traduit)",
      "Si en cas de perte du bulletin d'un document, le certificat peut passer, mais tous ces documents vous seront obligatoirement nécessaires",
    ],
    conseils: [
      "Ayez une lettre de confirmation de la part de votre université confirmant votre parcours",
      "N'hésitez pas à vous munir d'une lettre de motivation et un CV",
      "Incluez certains de vos certificats de formations",
    ],
  },
]

// Composant Badge
const Badge = ({ children, variant = "default", className = "" }) => {
  const baseClasses = "inline-flex items-center px-2 py-2 rounded-full text-xs font-medium"
  const variants = {
    default: "bg-blue-100 dark:bg-gray-700 dark:text-gray-200 text-blue-800",
    secondary: "bg-gray-100 dark:bg-gray-700 dark:text-gray-200 text-gray-800",
    success: "bg-green-100 border-green-800 border dark:bg-gray-700 dark:text-gray-200 text-green-800",
    warning: "bg-yellow-100 border-red-600 border dark:bg-gray-700 dark:text-gray-200 text-yellow-800",
    danger: "bg-gray-200 dark:bg-gray-700 dark:text-gray-200 text-red-800",
  }

  return <span className={`${baseClasses} ${variants[variant]} ${className}`}>{children}</span>
}

// Composant Card
const Card = ({ children, className = "" }) => {
  return <div className={`bg-white rounded-lg border border-gray-200 shadow-sm ${className}`}>{children}</div>
}

const CardHeader = ({ children }) => {
  return <div className="p-6 pb-4">{children}</div>
}

const CardContent = ({ children }) => {
  return <div className="px-6 pb-6">{children}</div>
}

const CardTitle = ({ children, className = "" }) => {
  return <h3 className={`text-lg font-semibold leading-none tracking-tight ${className}`}>{children}</h3>
}

// Composant Button
// const Button = ({ children, variant = "default", className = "", onClick, ...props }) => {
//   const baseClasses =
//     "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none"
//   const variants = {
//     default: "bg-blue-600 text-white hover:bg-blue-700 h-10 px-4 py-2",
//     outline: "border border-gray-300 bg-white hover:bg-gray-50 h-10 px-4 py-2",
//     success: "bg-green-600 text-white hover:bg-green-700 h-10 px-4 py-2",
//   }

//   return (
//     <button className={`${baseClasses} ${variants[variant]} ${className}`} onClick={onClick} {...props}>
//       {children}
//     </button>
//   )
// }

// PROCEDURE INSCRIPTION  
function RegistProcedure() {
  const [activeSection, setActiveSection] = useState("overview")

  // partie pour le couleur sur university step 
  const [expandedSteps, setExpandedSteps] = useState([])

  const toggleStep = (stepNumber) => {
    setExpandedSteps((prev) =>
      prev.includes(stepNumber) ? prev.filter((s) => s !== stepNumber) : [...prev, stepNumber],
    )
  }

  const getColorClasses = (color) => {
    const colors = {
      cyan: {
        bg: "bg-cyan-50",
        border: "border-cyan-200",
        header: "bg-cyan-100",
        text: "text-cyan-800",
        button: "bg-cyan-500 hover:bg-cyan-600",
        icon: "bg-cyan-100",
      },
      blue: {
        bg: "bg-blue-50",
        border: "border-blue-200",
        header: "bg-blue-100",
        text: "text-blue-800",
        button: "bg-blue-500 hover:bg-blue-600",
        icon: "bg-blue-100",
      },
      purple: {
        bg: "bg-purple-50",
        border: "border-purple-200",
        header: "bg-purple-100",
        text: "text-purple-800",
        button: "bg-purple-500 hover:bg-purple-600",
        icon: "bg-purple-100",
      },
    }
    return colors[color] || colors.cyan
  }
//////////////////////////////////////////////////////////////

  // const applicationSteps = [
  //    {
  //     step: 1,
  //     title: "Degré du diplôme et licence Bulletins scolaires des classes suivantes .",
  //     description: " 	4e secondaire	5e secondaire   6e secondaire   diplôme d'état de fin de 6e secondaire (document essentiel pour prouver l'obtention du niveau secondaire complet)   diplôme de fin de 6e primaire   Parfois, bulletin de 6e primaire, pour compléter le dossier scolaire ",
  //     details: "",
  //     icon: <FileText className="h-6 w-6 text-yellow-600" />,
  //     duration: "",
  //   },
  //    {
  //     step: 2,
  //     title: "Degré du Master, le programme du Master dans une université au Kenya, les candidats  doivent fournir un ensemble de documents académiques prouvant la complétion réussie de leurs études de niveau Bachelor (Licence), ainsi qu'un rappel de leur parcours secondaire.",
  //     description: " ",
  //     details: "",
  //     icon: <FileText className="h-6 w-6 text-yellow-600" />,
  //     duration: "",
  //   },
  //    {
  //     step: 2.1,
  //     title: "Documents requis pour le Master",
  //     description: "  	Tous les relevés de notes du Bachelor (Licence) Le diplôme du Bachelor   Le diplôme d'État de fin de 6e secondaire (obligatoire dans presque tous les dossiers)   Parfois, certains établissements demandent également :   Le diplôme de 6e primaire   Les bulletins de 4e à 6e secondaire ",
  //     details: "",
  //     icon: <FileText className="h-6 w-6 text-yellow-600" />,
  //     duration: "",
  //   },
  //    {
  //     step: 2.2,
  //     title: "À savoir :",
  //     description: "Certains programmes exigent aussi :	Un CV académique Une lettre de motivation   Parfois, deux lettres de recommandation ",
  //     details: "",
  //     icon: <FileText className="h-6 w-6 text-yellow-600" />,
  //     duration: "",
  //   },
  //    {
  //     step: 3,
  //     title: "Degré du Doctorat",
  //     description: " L'admission au Doctorat (PhD) au Kenya suit une procédure rigoureuse qui exige la présentation complète du parcours académique antérieur, depuis le secondaire jusqu'au Master.",
  //     details: "",
  //     icon: <FileText className="h-6 w-6 text-yellow-600" />,
  //     duration: "",
  //   },
  //    {
  //     step: 3.1,
  //     title: "Documents requis pour le Doctorat :",
  //     description: "  	Tous les relevés de notes du Master Le diplôme du Master Tous les relevés de notes du Bachelor (Licence)Le diplôme du Bachelor   Le diplôme d'État de fin de 6e secondaire (souvent demandé pour vérifier la cohérence du parcours scolaire) ",
  //     details: "",
  //     icon: <FileText className="h-6 w-6 text-yellow-600" />,
  //     duration: "",
  //   },
  //    {
  //     step: 3.2,
  //     title: "Documents complémentaires possibles :",
  //     description: "  	Lettre de motivation ou projet de recherche (parfois demandé dès l'inscription) 	CV académique et professionnel 	Lettres de recommandation (1 à3), souvent exigées pour les programmes de recherche À noter :La procédure peut varier légèrement dune université à une autre. Il est donc conseillé de vérifier les exigences précises sur le site officiel de l'établissement choisiTraduction, certification et enregistrement des documents académiquesAvant de soumettre votre demande d'admission dans une université kényane, il est obligatoire de faire traduire et certifier vos documents académiques.",
  //     details: "",
  //     icon: <FileText className="h-6 w-6 text-yellow-600" />,
  //     duration: "",
  //   },
  //    {
  //     step: 3.3,
  //     title: "Les exigences :",
  //     description: "  Les exigences varient en fonction de votre pays d'origine.1 Traduction et certification :   Pour certains pays africains, vous devez faire traduire vos documents en anglais et les faire certifier par l'ambassade de votre pays au Kenya. Pour d'autres pays, il est accepté que les documents soient : Traduit en français et en anglais   Certifiés par un avocat agréé ou un officier d'état civil local reconnu.",
  //     details: "",
  //     icon: <FileText className="h-6 w-6 text-yellow-600" />,
  //     duration: "",
  //   },
  //    {
  //     step: 3.4,
  //     title: "Les exigences :",
  //     description: " Il est fortement recommandé de vérifier les exigences spécifiques de votre pays sur le site du Kenya National Qualifications Authority (KNQA), souvent appelé Kenkié. 2 Enregistrement auprès du Kenkié (KNQA) : Le Kenkié est l'autorité officielle d'équivalence des diplômes au Kenya. Tous les documents doivent y être enregistrés avant votre inscription universitaire.	L'enregistrement se fait en ligne, via le site officiel du KNQA   Vous devez fournir des copies traduites et certifiées de vos  et relevésIl est aussi nécessaire d'ajouter une copie de votre passeport en cours de validité ",
  //     details: "",
  //     icon: <FileText className="h-6 w-6 text-yellow-600" />,
  //     duration: "",
  //   },
  //    {
  //     step: 4,
  //     title: "Langues acceptées pour l'inscription : ",
  //     description: " Les plateformes d'inscription acceptent généralement les documents en anglais et français   Toutefois, la version anglaise est toujours prioritaire Attention : Ne commencez pas votre inscription universitaire sans avoir ternniné cette étape denregistrement auprès du KNQA, car les universités exigent souvent le 	de référence délivré par ce bureau. ",
  //     details: "",
  //     icon: <FileText className="h-6 w-6 text-yellow-600" />,
  //     duration: "",
  //   },

  // ]

  const requiredDocuments = [
    {
      name: "Relevés de notes officiels",
      description: "Tous les relevés de notes du secondaire et/ou de l'enseignement supérieur",
      required: true,
      processing: "2-4 semaines pour certification",
      forLocal: true,
      forInternational: true,
    },
    {
      name: "Diplômes et certificats",
      description: "Copies certifiées de tous vos diplômes et certificats académiques",
      required: true,
      processing: "1-2 semaines pour certification",
      forLocal: true,
      forInternational: true,
    },
    {
      name: "Résultats du KCSE",
      description: "Kenya Certificate of Secondary Education pour les étudiants kenyans",
      required: true,
      processing: "Immédiat si déjà obtenu",
      forLocal: true,
      forInternational: false,
    },
    {
      name: "Équivalence des diplômes",
      description: "Évaluation d'équivalence par le Kenya National Qualifications Authority (KNQA)",
      required: true,
      processing: "4-6 semaines",
      forLocal: false,
      forInternational: true,
    },
    {
      name: "Pièce d'identité",
      description: "Carte d'identité nationale pour les Kenyans, passeport pour les internationaux",
      required: true,
      processing: "Immédiat",
      forLocal: true,
      forInternational: true,
    },
    {
      name: "Photos d'identité",
      description: "4-6 photos récentes format passeport",
      required: true,
      processing: "1 jour",
      forLocal: true,
      forInternational: true,
    },
    {
      name: "Lettre de motivation",
      description: "Expliquant votre intérêt pour le programme et vos objectifs",
      required: true,
      processing: "1-2 semaines de préparation",
      forLocal: true,
      forInternational: true,
    },
    {
      name: "Lettres de recommandation",
      description: "2-3 lettres de professeurs ou employeurs",
      required: false,
      processing: "2-3 semaines",
      forLocal: true,
      forInternational: true,
    },
    {
      name: "Preuve de compétence en anglais",
      description: "TOEFL, IELTS ou autre test reconnu pour les non-anglophones",
      required: false,
      processing: "4-8 semaines",
      forLocal: false,
      forInternational: true,
    },
    {
      name: "Preuve de financement",
      description: "Relevés bancaires, lettres de parrainage ou bourses",
      required: true,
      processing: "1-2 semaines",
      forLocal: false,
      forInternational: true,
    },
  ]

  const topUniversities = [
    {
      name: "University of Nairobi",
      type: "Publique",
      location: "Nairobi",
      founded: "1970",
      ranking: "1ère au Kenya",
      programs: "Plus de 400 programmes",
      website: "uonbi.ac.ke",
      admissionPeriod: "Janvier-Avril",
      fees: {
        local: "16,000-26,000 KES/semestre (régulier)",
        international: "200,000-450,000 KES/semestre",
      },
    },
    {
      name: "Kenyatta University",
      type: "Publique",
      location: "Nairobi",
      founded: "1985",
      ranking: "2ème au Kenya",
      programs: "Plus de 300 programmes",
      website: "ku.ac.ke",
      admissionPeriod: "Janvier-Mai",
      fees: {
        local: "16,000-26,000 KES/semestre (régulier)",
        international: "180,000-400,000 KES/semestre",
      },
    },
    {
      name: "Strathmore University",
      type: "Privée",
      location: "Nairobi",
      founded: "1961",
      ranking: "Top 3 au Kenya",
      programs: "Plus de 50 programmes",
      website: "strathmore.edu",
      admissionPeriod: "Janvier-Août (3 sessions)",
      fees: {
        local: "90,000-180,000 KES/semestre",
        international: "100,000-200,000 KES/semestre",
      },
    },
    {
      name: "Moi University",
      type: "Publique",
      location: "Eldoret",
      founded: "1984",
      ranking: "Top 5 au Kenya",
      programs: "Plus de 200 programmes",
      website: "mu.ac.ke",
      admissionPeriod: "Janvier-Avril",
      fees: {
        local: "16,000-26,000 KES/semestre (régulier)",
        international: "150,000-350,000 KES/semestre",
      },
    },
    {
      name: "Jomo Kenyatta University",
      type: "Publique",
      location: "Juja",
      founded: "1994",
      ranking: "Top 5 au Kenya",
      programs: "Plus de 150 programmes",
      website: "jkuat.ac.ke",
      admissionPeriod: "Janvier-Avril",
      fees: {
        local: "16,000-26,000 KES/semestre (régulier)",
        international: "150,000-350,000 KES/semestre",
      },
    },
    {
      name: "Mount Kenya University",
      type: "Privée",
      location: "Thika",
      founded: "2008",
      ranking: "Top 10 au Kenya",
      programs: "Plus de 100 programmes",
      website: "mku.ac.ke",
      admissionPeriod: "Janvier-Septembre (4 sessions)",
      fees: {
        local: "70,000-150,000 KES/semestre",
        international: "90,000-180,000 KES/semestre",
      },
    },
  ]

  const importantDates = [
    {
      period: "Janvier-Février",
      event: "Publication des résultats du KCSE",
      details: "Les résultats du Kenya Certificate of Secondary Education sont publiés",
      forLocal: true,
      forInternational: false,
    },
    {
      period: "Mars-Avril",
      event: "Ouverture des inscriptions KUCCPS",
      details: "Période d'inscription au Kenya Universities and Colleges Central Placement Service",
      forLocal: true,
      forInternational: false,
    },
    {
      period: "Mai",
      event: "Première révision des choix de programmes",
      details: "Possibilité de modifier vos choix de programmes universitaires",
      forLocal: true,
      forInternational: false,
    },
    {
      period: "Juin",
      event: "Annonce des placements universitaires",
      details: "Publication des résultats d'admission par le KUCCPS",
      forLocal: true,
      forInternational: false,
    },
    {
      period: "Juillet",
      event: "Émission des lettres d'admission",
      details: "Les universités envoient les lettres d'admission officielles",
      forLocal: true,
      forInternational: true,
    },
    {
      period: "Août-Septembre",
      event: "Début de l'année académique",
      details: "Orientation et début des cours pour le premier semestre",
      forLocal: true,
      forInternational: true,
    },
    {
      period: "Octobre-Novembre",
      event: "Inscriptions pour le deuxième semestre",
      details: "Pour les programmes qui permettent une entrée en milieu d'année",
      forLocal: true,
      forInternational: true,
    },
    {
      period: "Décembre",
      event: "Vacances universitaires",
      details: "Fin du premier semestre et pause de fin d'année",
      forLocal: true,
      forInternational: true,
    },
  ]

  const scholarshipOptions = [
    {
      name: "Higher Education Loans Board (HELB)",
      provider: "Gouvernement kenyan",
      eligibility: "Étudiants kenyans uniquement",
      amount: "Jusqu'à 60,000 KES par an",
      deadline: "Varie selon l'année académique",
      website: "helb.co.ke",
    },
    {
      name: "Bourses Mastercard Foundation",
      provider: "Mastercard Foundation",
      eligibility: "Étudiants africains avec besoins financiers",
      amount: "Couverture complète (frais + allocation)",
      deadline: "Février-Mars chaque année",
      website: "mastercardfdn.org/scholars-program",
    },
    {
      name: "Bourses d'excellence universitaires",
      provider: "Universités individuelles",
      eligibility: "Basé sur les résultats académiques",
      amount: "Réduction partielle ou totale des frais",
      deadline: "Varie selon l'université",
      website: "Sites des universités respectives",
    },
    {
      name: "Bourses du gouvernement kenyan",
      provider: "Ministère de l'Éducation",
      eligibility: "Étudiants kenyans et internationaux",
      amount: "Variable selon les programmes",
      deadline: "Généralement en mars",
      website: "education.go.ke",
    },
    {
      name: "Bourses DAAD",
      provider: "Office allemand d'échanges universitaires",
      eligibility: "Étudiants internationaux",
      amount: "Couverture partielle ou complète",
      deadline: "Octobre-Décembre",
      website: "daad.de",
    },
  ]

  const faqItems = [
    {
      question: "Quelle est la différence entre les programmes réguliers et parallèles ?",
      answer:
        "Les programmes réguliers sont financés par le gouvernement et ont des frais de scolarité réduits, mais sont très compétitifs. Les programmes parallèles (ou Module II) sont autofinancés avec des frais plus élevés, mais des critères d'admission moins stricts.",
    },
    {
      question: "Puis-je m'inscrire dans plusieurs universités à la fois ?",
      answer:
        "Via le KUCCPS, vous pouvez choisir jusqu'à 6 programmes dans différentes universités, mais vous ne serez placé que dans un seul. Pour les candidatures directes aux universités privées, vous pouvez postuler à plusieurs établissements, mais devrez choisir une fois accepté.",
    },
    {
      question: "Les étudiants internationaux peuvent-ils obtenir des bourses ?",
      answer:
        "Oui, plusieurs bourses sont disponibles pour les étudiants internationaux, notamment via la Mastercard Foundation, les programmes d'échange universitaire et certaines bourses spécifiques aux universités. Les critères varient selon les programmes.",
    },
    {
      question: "Quel est le niveau minimum requis pour être admis dans une université kenyane ?",
      answer:
        "Pour les universités publiques via le KUCCPS, un minimum de C+ au KCSE est généralement requis. Pour les universités privées et les programmes parallèles, les exigences peuvent être plus flexibles. Les étudiants internationaux doivent avoir l'équivalent d'un diplôme de fin d'études secondaires.",
    },
    {
      question: "Les cours sont-ils dispensés en anglais ?",
      answer:
        "Oui, l'anglais est la langue d'enseignement dans toutes les universités kenyanes. Le swahili est également parlé, mais les cours, examens et travaux académiques sont en anglais.",
    },
    {
      question: "Combien coûtent les études universitaires au Kenya ?",
      answer:
        "Les frais varient considérablement. Pour les étudiants kenyans en programmes réguliers dans les universités publiques, environ 16,000-26,000 KES par semestre. Pour les programmes parallèles et les universités privées, entre 70,000 et 180,000 KES par semestre. Pour les étudiants internationaux, entre 150,000 et 450,000 KES par semestre selon l'université et le programme.",
    },
  ]

  const sections = [
    { id: "overview", name: "Aperçu", icon: <BookOpen className="h-5 w-5" /> },
    { id: "application", name: "Procédure", icon: <FileText className="h-5 w-5" /> },
    // { id: "documents", name: "Documents", icon: <CheckCircle className="h-5 w-5" /> },
    // { id: "universities", name: "Universités", icon: <Building className="h-5 w-5" /> },
    // { id: "dates", name: "Calendrier", icon: <Calendar className="h-5 w-5" /> },
    { id: "scholarships", name: "Bourses", icon: <DollarSign className="h-5 w-5" /> },
    { id: "faq", name: "FAQ", icon: <AlertTriangle className="h-5 w-5" /> },
    { id: "resources", name: "Ressources", icon: <Download className="h-5 w-5" /> },
  ]

  return (
    <div className="min-h-screen mtCGfreeUniv bg-gradient-to-br from-indigo-50 to-purple-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-green-700 p-3 rounded-lg">
                <GraduationCap className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-blue-500">
                 Guide Professionnel pour Documents d'Admission Universitaire
                </h1>
                <p className="text-gray-600 dark:text-gray-500 mt-1">
                Diploma/Bachelor, Master, et PhD - Tout ce que vous devez savoir
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white sticky dark:bg-gray-900 top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center space-x-2 py-4 px-2 border-b-2 font-medium dark:text-gray-500 whitespace-nowrap ${
                  activeSection === section.id
                    ? "border-door text-door-dark"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                {section.icon}
                <span>{section.name}</span>
              </button>
            ))}
          </div>
        </div>
      </nav>

      <div className="max-w-7xl dark:bg-gray-900 mx-auto px-4 sm:px-6 lg:px-8 py-8 mb-10">
        {/* Section Aperçu */}
        {activeSection === "overview" && (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-door mb-4">
                Système Universitaire Kenyan
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                Plusieurs niveaux d'études vous sont proposés
              </p>
            </div>

             {/* Système éducatif */}
             <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="dark:bg-gray-800 dark:border-gray-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-door" />
                    Système 8-4-4
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-400">
                    Le système éducatif kenyan traditionnel comprend 8 années d'école primaire, 4 années d'école
                    secondaire et 4 années d'université pour la plupart des programmes de premier cycle.
                  </p>
                </CardContent>
              </Card>

              <Card className="dark:bg-gray-800 dark:border-gray-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-red-600" />
                    Année Académique
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-400">
                    L'année académique au Kenya commence généralement en septembre et se termine en juin, avec deux
                    semestres par an. Les inscriptions principales se font entre mai et août.
                  </p>
                </CardContent>
              </Card>

              <Card className="dark:bg-gray-800 dark:border-gray-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building className="h-5 w-5 text-red-600" />
                    Types d'Universités
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-400">
                    Le Kenya dispose d'universités publiques (financées par l'État) et privées. Les universités
                    publiques sont généralement moins chères mais plus compétitives, tandis que les universités privées
                    offrent souvent plus de flexibilité.
                  </p>
                </CardContent>
              </Card>
            </div>

             <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="dark:bg-gray-800 dark:border-gray-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-door" />
                    Le diplôme (Diploma) :
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-400">
                   Il s'agit d'une formation de courte durée généralement entre 1 et 2 ans accessible après le secondaire.
                  </p>
                </CardContent>
              </Card>

              <Card className="dark:bg-gray-800 dark:border-gray-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                     <BookOpen className="h-5 w-5 text-door" />
                    La licence (Bachelor) :
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-400">
                   Ce programme dure généralement 3 à 4 ans. Il est accessible après l'obtention d'un diplôme d'études secondaires.
                  </p>
                </CardContent>
              </Card>    
              
              <Card className="dark:bg-gray-800 dark:border-gray-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-door" />
                    Le master (Master) 
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-400">
                  Ce cycle dure entre 1 et 2 ans, selon le programme et l'université.
                     </p>
                </CardContent>
              </Card>

              <Card className="dark:bg-gray-800 dark:border-gray-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-door" />
                    	Le doctorat (Doctorate/PhD)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-400">
                    Ce programme dure entre 3 et 5 ans, parfois plus, selon le programme de l'université.
                  </p>
                </CardContent>
              </Card>
            </div>

           

            {/* Statistiques */}
            {/* <Card className="p-6 dark:bg-gray-800 dark:border-gray-700">
              <h3 className="text-xl font-semibold mb-6 dark:text-gray-300">Chiffres clés</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-red-600 mb-2">31+</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Universités publiques</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">30+</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Universités privées</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">500k+</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Étudiants universitaires</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">5k+</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Étudiants internationaux</div>
                </div>
              </div>
            </Card> */}

            {/* Différences entre étudiants locaux et internationaux */}
            <Card className="p-6 dark:bg-gray-800 dark:border-gray-700">
              <h3 className="text-xl font-semibold mb-6 dark:text-gray-300">
                Différences clés dans le processus d'admission
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-medium text-lg mb-4 flex items-center">
                    <User className="h-5 w-5 mr-2 text-blue-600" />
                    Étudiants Kenyans
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Admission centralisée via le KUCCPS pour les universités publiques
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Basé sur les résultats du KCSE (Kenya Certificate of Secondary Education)
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Frais de scolarité subventionnés pour les programmes réguliers
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Possibilité de prêts étudiants via le HELB (Higher Education Loans Board)
                      </span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-lg mb-4 flex items-center">
                    <Globe className="h-5 w-5 mr-2 text-purple-600" />
                    Étudiants Internationaux
                  </h4>
                  <ul className="space-y-3">
                    
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Vérification d'équivalence des diplômes étrangers
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Frais de scolarité plus élevés (tarifs internationaux)
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Exigences supplémentaires (visa étudiant, preuve de financement, etc.)
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Section Procédure d'inscription */}
        {activeSection === "application" && (
             <div className="min-h-screen">
      {/* Header */}
       

      {/* Main Content */}
      <main className="max-w-6xl mx-auto">
        {/* Introduction */}
        <div className="bg-white border-l-blue-500 rounded-lg shadow-sm mb-12">
          <div className="p-8">
            <p className="text-lg text-gray-600 leading-relaxed text-pretty">
              Ce guide présente de manière structurée tous les documents requis pour chaque niveau d'études universitaires. 
              Cliquez sur chaque section pour découvrir les exigences spécifiques à votre niveau d'admission.
            </p>
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-8">
          {universitySteps.map((step) => {
            const isExpanded = expandedSteps.includes(step.step)
            const colorClasses = getColorClasses(step.color)

            return (
              <div key={step.step} className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                {/* Step Header */}
                <div
                  className={`${colorClasses.bg} ${colorClasses.border} border-b cursor-pointer transition-all duration-200 hover:shadow-md`}
                  onClick={() => toggleStep(step.step)}
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                          
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                              
                            <h2 className={`text-2xl font-bold ${colorClasses.text}`}>{step.level}</h2>
                          </div>
                          <p className="text-gray-600 text-pretty">{step.description}</p>
                        </div>
                      </div>
                      <button
                        className={`${colorClasses.button} text-white px-6 py-3 rounded-lg transition-all duration-200 flex items-center gap-2 font-medium`}
                      >
                        <span>{isExpanded ? "Masquer" : "Afficher"}</span>
                        <svg
                          className={`h-5 w-5 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Step Content */}
                <div className={`transition-all duration-500 ease-in-out ${isExpanded ? "max-h-none opacity-100" : "max-h-0 opacity-0 overflow-hidden"}`}>
                  <div className="p-4 space-y-8">
                    {/* Documents Required */}
                    <div className="bg-gray-50 rounded-lg border">
                      <div className="p-4 border-b bg-gray-100">
                        <h3 className="flex items-center gap-2 font-semibold text-gray-900">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                          Documents Requis
                        </h3>
                      </div>
                      <div className="p-2">
                        <div className="grid md:grid-cols-2 gap-4">
                          {step.documents.map((doc, index) => (
                            <div
                              key={index}
                              className="flex items-start gap-3 p-3 bg-white rounded-lg border hover:shadow-sm transition-shadow duration-150"
                            >
                              <div className={`w-2 h-2 ${colorClasses.button.split(" ")[0]} rounded-full mt-2 flex-shrink-0`}></div>
                              <span className="text-gray-700">{doc}</span>
                            </div>
                          ))}
                        </div>
                        {step.note && (
                          <div className={`${colorClasses.bg} ${colorClasses.border} border p-4 rounded-lg mt-6`}>
                            <p className={`text-sm ${colorClasses.text} font-medium`}>
                              <strong>Note importante :</strong> {step.note}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Conseils */}
                    {step.conseils && step.conseils.length > 0 && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="p-4 border-b border-yellow-200 bg-yellow-100">
                          <h3 className="flex items-center gap-2 font-semibold text-yellow-800">
                            <Lightbulb className="h-5 w-5 text-yellow-600" />
                            Conseils Pratiques
                          </h3>
                        </div>
                        <div className="p-6">
                          <ul className="space-y-3">
                            {step.conseils.map((conseil, index) => (
                              <li key={index} className="flex items-start gap-3">
                                <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></div>
                                <span className="text-yellow-800">{conseil}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}

                    {/* Attention */}
                    <div className="bg-red-50 border border-red-200 rounded-lg">
                      <div className="p-4 border-b border-red-200 bg-red-100">
                        <h3 className="flex items-center gap-2 font-semibold text-red-800">
                          <AlertTriangle className="h-5 w-5 text-red-600" />
                          Points d'Attention Importants
                        </h3>
                      </div>
                      <div className="p-6">
                        <ul className="space-y-4">
                          {step.attention.map((point, index) => (
                            <li key={index} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-red-100">
                              <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                              <span className="text-red-800 text-sm leading-relaxed">{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Footer */}
        <footer className="bg-gradient-to-r from-gray-100 to-gray-200 p-8 rounded-xl mt-16 text-center">
          <div className="max-w-2xl mx-auto">
            <p className="text-gray-600 text-pretty leading-relaxed">
              Ce guide est fourni à titre informatif et basé sur les exigences générales. Vérifiez toujours les exigences spécifiques auprès de l'établissement d'enseignement concerné avant de soumettre votre dossier d'admission.
            </p>
          </div>
        </footer>
      </main>
    </div>
        )}

        {/* Section Documents */}
        {activeSection === "documents" && (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-green-500 mb-4">Documents Requis</h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                Préparez tous les documents nécessaires pour votre candidature universitaire au Kenya.
              </p>
            </div>

            {/* Filtres pour documents */}
            <div className="flex justify-center mb-6">
              <div className="inline-flex rounded-md shadow-sm">
                <button
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  onClick={() => {}}
                >
                  Tous
                </button>
                <button
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border-t border-b border-gray-300 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  onClick={() => {}}
                >
                  Étudiants Kenyans
                </button>
                <button
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  onClick={() => {}}
                >
                  Étudiants Internationaux
                </button>
              </div>
            </div>

            {/* Liste des documents */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {requiredDocuments.map((doc, index) => (
                <Card key={index} className="p-4 dark:bg-gray-800 dark:border-gray-700">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h4 className="font-medium dark:text-green-500">{doc.name}</h4>
                        {doc.required ? (
                          <Badge variant="danger">Obligatoire</Badge>
                        ) : (
                          <Badge variant="secondary">Recommandé</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{doc.description}</p>
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-500">
                        <Clock className="h-3 w-3 mr-1" />
                        {doc.processing}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {doc.forLocal && (
                          <Badge variant="success" className="text-xs">
                            Étudiants Kenyans
                          </Badge>
                        )}
                        {doc.forInternational && (
                          <Badge variant="warning" className="text-xs">
                            Étudiants Internationaux
                          </Badge>
                        )}
                      </div>
                    </div>
                    {doc.required && <CheckCircle className="h-5 w-5 text-green-600 mt-1" />}
                  </div>
                </Card>
              ))}
            </div>

            {/* Conseils pour la préparation des documents */}
            <Card className="p-6 dark:bg-gray-800 dark:border-gray-700">
              <h3 className="text-xl font-semibold mb-4 dark:text-gray-300">
                Conseils pour la préparation des documents
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-3 text-green-700 dark:text-green-500">À faire</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Commencer la préparation des documents au moins 3 mois à l'avance
                      </span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Faire certifier toutes les copies de documents officiels
                      </span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Traduire les documents non-anglais par un traducteur assermenté
                      </span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Scanner tous les documents en haute résolution
                      </span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Conserver des copies de tous les documents soumis
                      </span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-3 text-red-700 dark:text-door-dark">À éviter</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center">
                      <AlertTriangle className="h-4 w-4 text-red-600 mr-2" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Soumettre des documents incomplets ou illisibles
                      </span>
                    </li>
                    <li className="flex items-center">
                      <AlertTriangle className="h-4 w-4 text-red-600 mr-2" />
                      <span className="text-gray-700 dark:text-gray-300">Utiliser des traductions non officielles</span>
                    </li>
                    <li className="flex items-center">
                      <AlertTriangle className="h-4 w-4 text-red-600 mr-2" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Attendre la dernière minute pour rassembler les documents
                      </span>
                    </li>
                    <li className="flex items-center">
                      <AlertTriangle className="h-4 w-4 text-red-600 mr-2" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Envoyer des documents originaux sans en garder de copies
                      </span>
                    </li>
                    <li className="flex items-center">
                      <AlertTriangle className="h-4 w-4 text-red-600 mr-2" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Négliger les exigences spécifiques de chaque université
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Section Universités */}
        {activeSection === "universities" && (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-green-500 mb-4">
                Principales Universités Kenyanes
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                Découvrez les meilleures universités du Kenya, leurs programmes et leurs procédures d'admission.
              </p>
            </div>

            {/* Liste des universités */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {topUniversities.map((university, index) => (
                <Card key={index} className="overflow-hidden dark:bg-gray-800 dark:border-gray-700">
                  <div
                    className="h-40 bg-cover bg-center"
                    style={{ backgroundImage: `url('/placeholder.svg?height=200&width=300')` }}
                  />
                  <CardHeader>
                    <div className="flex justify-between items-center mb-2">
                      <CardTitle>{university.name}</CardTitle>
                      <Badge variant={university.type === "Publique" ? "default" : "secondary"}>
                        {university.type}
                      </Badge>
                    </div>
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                      <MapPin className="h-4 w-4 mr-1" />
                      {university.location}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Fondée en:</span>
                        <span className="dark:text-gray-300">{university.founded}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Classement:</span>
                        <span className="dark:text-gray-300">{university.ranking}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Programmes:</span>
                        <span className="dark:text-gray-300">{university.programs}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Période d'admission:</span>
                        <span className="dark:text-gray-300">{university.admissionPeriod}</span>
                      </div>
                      <div className="pt-2 border-t dark:border-gray-700">
                        <span className="text-gray-500 dark:text-gray-400">Frais (étudiants locaux):</span>
                        <div className="font-medium dark:text-gray-300">{university.fees.local}</div>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Frais (internationaux):</span>
                        <div className="font-medium dark:text-gray-300">{university.fees.international}</div>
                      </div>
                      <div className="pt-2">
                        <a
                          href={`https://${university.website}`}
                          className="text-red-600 hover:underline flex items-center"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Globe className="h-4 w-4 mr-1" />
                          {university.website}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Critères de choix */}
            <Card className="p-6 dark:bg-gray-800 dark:border-gray-700">
              <h3 className="text-xl font-semibold mb-6 dark:text-gray-300">Critères pour choisir votre université</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  {
                    factor: "Accréditation",
                    importance: "Critique",
                    description: "Vérifier la reconnaissance officielle par le gouvernement kenyan",
                    icon: <CheckCircle className="h-5 w-5 text-green-600" />,
                  },
                  {
                    factor: "Programmes offerts",
                    importance: "Élevée",
                    description: "Correspondance avec vos objectifs académiques et professionnels",
                    icon: <BookOpen className="h-5 w-5 text-blue-600" />,
                  },
                  {
                    factor: "Réputation",
                    importance: "Élevée",
                    description: "Classements nationaux et internationaux, avis d'anciens étudiants",
                    icon: <Star className="h-5 w-5 text-yellow-600" />,
                  },
                  {
                    factor: "Coûts",
                    importance: "Élevée",
                    description: "Frais de scolarité, coût de la vie, bourses disponibles",
                    icon: <DollarSign className="h-5 w-5 text-green-600" />,
                  },
                  {
                    factor: "Localisation",
                    importance: "Moyenne",
                    description: "Proximité des commodités, sécurité du quartier, transport",
                    icon: <MapPin className="h-5 w-5 text-red-600" />,
                  },
                  {
                    factor: "Infrastructures",
                    importance: "Moyenne",
                    description: "Bibliothèques, laboratoires, équipements sportifs, WiFi",
                    icon: <Shield className="h-5 w-5 text-purple-600" />,
                  },
                ].map((criterion, index) => (
                  <div key={index} className="border dark:border-gray-700 rounded-lg p-4 flex flex-col items-start">
                    <div className="flex items-center mb-3">
                      {criterion.icon}
                      <h4 className="font-medium ml-2 dark:text-gray-300">{criterion.factor}</h4>
                    </div>
                    <Badge
                      variant={
                        criterion.importance === "Critique"
                          ? "danger"
                          : criterion.importance === "Élevée"
                            ? "warning"
                            : "secondary"
                      }
                      className="mb-3"
                    >
                      Importance: {criterion.importance}
                    </Badge>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{criterion.description}</p>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Section Calendrier */}
        {activeSection === "dates" && (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-green-500 mb-4">
                Calendrier et Dates Importantes
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                Planifiez votre candidature en respectant ces dates clés du processus d'admission universitaire au
                Kenya.
              </p>
            </div>

            {/* Calendrier d'admission */}
            <Card className="p-6 dark:bg-gray-800 dark:border-gray-700">
              <h3 className="text-xl font-semibold mb-6 dark:text-gray-300">Calendrier d'admission 2024</h3>
              <div className="space-y-4">
                {importantDates.map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100 px-3 py-1 rounded-full text-sm font-medium w-36 text-center">
                      {item.period}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium dark:text-gray-300">{item.event}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{item.details}</div>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {item.forLocal && (
                        <Badge variant="success" className="text-xs">
                          Locaux
                        </Badge>
                      )}
                      {item.forInternational && (
                        <Badge variant="warning" className="text-xs">
                          Internationaux
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Dates limites importantes */}
            <Card className="p-6 dark:bg-gray-800 dark:border-gray-700">
              <h3 className="text-xl font-semibold mb-6 dark:text-gray-300">Dates limites importantes</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-4 flex items-center">
                    <AlertTriangle className="h-5 w-5 mr-2 text-red-600" />
                    Pour les étudiants kenyans
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-center justify-between pb-2 border-b dark:border-gray-700">
                      <span className="text-gray-700 dark:text-gray-300">Inscription au KUCCPS</span>
                      <Badge variant="danger">15 avril 2024</Badge>
                    </li>
                    <li className="flex items-center justify-between pb-2 border-b dark:border-gray-700">
                      <span className="text-gray-700 dark:text-gray-300">Révision des choix</span>
                      <Badge variant="danger">30 avril 2024</Badge>
                    </li>
                    <li className="flex items-center justify-between pb-2 border-b dark:border-gray-700">
                      <span className="text-gray-700 dark:text-gray-300">Demande de prêt HELB</span>
                      <Badge variant="danger">30 juin 2024</Badge>
                    </li>
                    <li className="flex items-center justify-between">
                      <span className="text-gray-700 dark:text-gray-300">Inscription sur campus</span>
                      <Badge variant="danger">15 août 2024</Badge>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-4 flex items-center">
                    <AlertTriangle className="h-5 w-5 mr-2 text-red-600" />
                    Pour les étudiants internationaux
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-center justify-between pb-2 border-b dark:border-gray-700">
                      <span className="text-gray-700 dark:text-gray-300">Candidature (1er semestre)</span>
                      <Badge variant="danger">31 mai 2024</Badge>
                    </li>
                    <li className="flex items-center justify-between pb-2 border-b dark:border-gray-700">
                      <span className="text-gray-700 dark:text-gray-300">Candidature (2ème semestre)</span>
                      <Badge variant="danger">31 octobre 2024</Badge>
                    </li>
                    <li className="flex items-center justify-between pb-2 border-b dark:border-gray-700">
                      <span className="text-gray-700 dark:text-gray-300">Demande de visa étudiant</span>
                      <Badge variant="danger">31 juillet 2024</Badge>
                    </li>
                    <li className="flex items-center justify-between">
                      <span className="text-gray-700 dark:text-gray-300">Inscription sur campus</span>
                      <Badge variant="danger">31 août 2024</Badge>
                    </li>
                  </ul>
                </div>
              </div>
            </Card>

            {/* Conseils de planification */}
            <Card className="p-6 dark:bg-gray-800 dark:border-gray-700">
              <h3 className="text-xl font-semibold mb-4 dark:text-gray-300">Conseils de planification</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-blue-50 dark:bg-gray-700 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-700 dark:text-blue-300 mb-3">6-12 mois avant</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">Rechercher les universités et programmes</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">Préparer les tests standardisés</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">Commencer à rassembler les documents</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-green-50 dark:bg-gray-700 p-4 rounded-lg">
                  <h4 className="font-medium text-green-700 dark:text-green-300 mb-3">3-6 mois avant</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">Finaliser la liste des universités</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">Compléter les candidatures</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">Demander les lettres de recommandation</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-yellow-50 dark:bg-gray-700 p-4 rounded-lg">
                  <h4 className="font-medium text-yellow-700 dark:text-yellow-300 mb-3">1-3 mois avant</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-yellow-600 mr-2 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">Soumettre les candidatures</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-yellow-600 mr-2 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">Demander le visa étudiant</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-yellow-600 mr-2 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">Rechercher un logement</span>
                    </li>
                  </ul>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Section Bourses */}
        {activeSection === "scholarships" && (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-door mb-4">
                Bourses et Options de Financement
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                Découvrez les différentes options de financement disponibles pour vos études universitaires au Kenya.
              </p>
            </div>

             {/* Guide pour postuler aux bourses */}
            <Card className="p-6 dark:bg-gray-800 dark:border-gray-700">
              <h3 className="text-xl font-semibold mb-6 dark:text-gray-300">Comment postuler pour une bourse</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="bg-blue-100 dark:bg-blue-900 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                    <span className="text-blue-600 dark:text-blue-300 font-bold">1</span>
                  </div>
                  <h4 className="font-medium dark:text-gray-300">Recherche</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Identifier les bourses correspondant à votre profil
                  </p>
                </div>
                <div className="text-center">
                  <div className="bg-green-100 dark:bg-green-900 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                    <span className="text-green-600 dark:text-green-300 font-bold">2</span>
                  </div>
                  <h4 className="font-medium dark:text-gray-300">Préparation</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Rassembler tous les documents requis</p>
                </div>
                <div className="text-center">
                  <div className="bg-yellow-100 dark:bg-yellow-900 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                    <span className="text-yellow-600 dark:text-yellow-300 font-bold">3</span>
                  </div>
                  <h4 className="font-medium dark:text-gray-300">Candidature</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Soumettre votre dossier avant la date limite
                  </p>
                </div>
                <div className="text-center">
                  <div className="bg-purple-100 dark:bg-purple-900 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                    <span className="text-purple-600 dark:text-purple-300 font-bold">4</span>
                  </div>
                  <h4 className="font-medium dark:text-gray-300">Suivi</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Suivre l'état de votre candidature</p>
                </div>
              </div>
            </Card>

            {/* Options de bourses */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {scholarshipOptions.map((scholarship, index) => (
                <Card key={index} className="p-6 dark:bg-gray-800 dark:border-gray-700">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-lg font-semibold dark:text-gray-300">{scholarship.name}</h3>
                    <Badge variant="default">{scholarship.provider}</Badge>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-400">Éligibilité: </span>
                      <span className="text-sm text-gray-600 dark:text-gray-300">{scholarship.eligibility}</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-400">Montant: </span>
                      <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                        {scholarship.amount}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-400">Date limite: </span>
                      <span className="text-sm text-red-600 dark:text-red-400 font-medium">{scholarship.deadline}</span>
                    </div>
                    <div className="pt-2">
                      <a
                        href={`https://${scholarship.website}`}
                        className="text-red-600 hover:underline flex items-center text-sm"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Globe className="h-4 w-4 mr-1" />
                        {scholarship.website}
                      </a>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

           

            {/* Conseils pour obtenir une bourse */}
            <Card className="p-6 dark:bg-gray-800 dark:border-gray-700">
              <h3 className="text-xl font-semibold mb-4 dark:text-gray-300">Conseils pour maximiser vos chances</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-3 text-green-700 dark:text-green-500">Stratégies gagnantes</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Maintenir d'excellents résultats académiques
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Participer à des activités extrascolaires et communautaires
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Rédiger une lettre de motivation convaincante
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Obtenir de solides lettres de recommandation
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">Postuler à plusieurs bourses différentes</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-3 text-red-700 dark:text-door-dark">Erreurs à éviter</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start">
                      <AlertTriangle className="h-4 w-4 text-red-600 mr-2 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Attendre la dernière minute pour postuler
                      </span>
                    </li>
                    <li className="flex items-start">
                      <AlertTriangle className="h-4 w-4 text-red-600 mr-2 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">Négliger les critères d'éligibilité</span>
                    </li>
                    <li className="flex items-start">
                      <AlertTriangle className="h-4 w-4 text-red-600 mr-2 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">Soumettre des documents incomplets</span>
                    </li>
                    <li className="flex items-start">
                      <AlertTriangle className="h-4 w-4 text-red-600 mr-2 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Copier des lettres de motivation génériques
                      </span>
                    </li>
                    <li className="flex items-start">
                      <AlertTriangle className="h-4 w-4 text-red-600 mr-2 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Ne pas suivre les instructions spécifiques
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Section FAQ */}
        {activeSection === "faq" && (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-door mb-4">
                Questions Fréquemment Posées
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                Trouvez des réponses aux questions les plus courantes sur les procédures d'inscription universitaire au
                Kenya.
              </p>
            </div>

            {/* FAQ Items */}
            <div className="space-y-4">
              {faqItems.map((item, index) => (
                <Card key={index} className="dark:bg-gray-800 dark:border-gray-700">
                  <div className="p-6">
                    <h3 className="text-lg font-semibold mb-3 dark:text-gray-300">{item.question}</h3>
                    <p className="text-gray-600 dark:text-gray-400">{item.answer}</p>
                  </div>
                </Card>
              ))}
            </div>

            {/* Contact pour plus d'informations */}
            <Card className="p-6 dark:bg-gray-800 dark:border-gray-700">
              <h3 className="text-xl font-semibold mb-4 dark:text-gray-300">Vous avez d'autres questions ?</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                N'hésitez pas à contacter les services d'admission des universités ou les organismes officiels pour
                obtenir des informations spécifiques à votre situation.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-blue-50 dark:bg-gray-700 rounded-lg">
                  <Phone className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                  <div className="font-medium dark:text-gray-300">KUCCPS</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">+254 20 222 2022</div>
                </div>
                <div className="text-center p-4 bg-green-50 dark:bg-gray-700 rounded-lg">
                  <Globe className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <div className="font-medium dark:text-gray-300">CUE</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">cue.or.ke</div>
                </div>
                <div className="text-center p-4 bg-purple-50 dark:bg-gray-700 rounded-lg">
                  <DollarSign className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                  <div className="font-medium dark:text-gray-300">HELB</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">helb.co.ke</div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Section Ressources */}
        {activeSection === "resources" && (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-door mb-4">Ressources Utiles</h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                Accédez aux ressources officielles et aux outils pratiques pour votre inscription universitaire au
                Kenya.
              </p>
            </div>

            {/* Liens officiels */}
            {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                // {
                //   title: "Sites Officiels",
                //   links: [
                //     {
                //       name: "Kenya Universities and Colleges Central Placement Service (KUCCPS)",
                //       url: "https://www.kuccps.net",
                //     },
                //     { name: "Commission for University Education (CUE)", url: "https://www.cue.or.ke" },
                //     { name: "Higher Education Loans Board (HELB)", url: "https://www.helb.co.ke" },
                //     { name: "Ministry of Education Kenya", url: "https://www.education.go.ke" },
                //   ],
                // },
                // {
                //   title: "Guides et Ressources",
                //   links: [
                //     { name: "Guide complet des universités kenyanes", url: "#" },
                //     { name: "Classement des universités au Kenya", url: "#" },
                //     { name: "Calculateur de frais universitaires", url: "#" },
                //     { name: "Comparateur de programmes d'études", url: "#" },
                //   ],
                // },
                // {
                //   title: "Pour Étudiants Internationaux",
                //   links: [
                //     { name: "Immigration Kenya - Visas étudiants", url: "https://immigration.go.ke" },
                //     { name: "Association des étudiants internationaux au Kenya", url: "#" },
                //     { name: "Guide de la vie étudiante au Kenya", url: "#" },
                //     { name: "Logements étudiants au Kenya", url: "#" },
                //   ],
                // },
              ].map((category, index) => (
                <Card key={index} className="dark:bg-gray-800 dark:border-gray-700">
                  <CardHeader>
                    <CardTitle>{category.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {category.links.map((link, linkIndex) => (
                        <li key={linkIndex}>
                          <a
                            href={link.url}
                            className="text-red-600 hover:underline flex items-center gap-1"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ChevronRight className="h-4 w-4" />
                            <span className="text-sm">{link.name}</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div> */}

            {/* Téléchargements */}
            {/* <Card className="p-6 dark:bg-gray-800 dark:border-gray-700">
              <h3 className="text-xl font-semibold mb-6 dark:text-gray-300">Documents à télécharger</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { name: "Guide d'inscription KUCCPS", size: "2.5 MB", format: "PDF" },
                  { name: "Liste des universités accréditées", size: "1.8 MB", format: "PDF" },
                  { name: "Formulaire de demande de bourse", size: "0.5 MB", format: "PDF" },
                  { name: "Checklist documents requis", size: "0.3 MB", format: "PDF" },
                ].map((doc, index) => (
                  <div
                    key={index}
                    className="border dark:border-gray-700 rounded-lg p-4 flex flex-col items-center text-center"
                  >
                    <Download className="h-8 w-8 text-red-600 mb-2" />
                    <h4 className="font-medium mb-2 dark:text-gray-300">{doc.name}</h4>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      {doc.format} • {doc.size}
                    </div>
                    <Button variant="outline" className="w-full">
                      Télécharger
                    </Button>
                  </div>
                ))}
              </div>
            </Card> */}

            {/* Applications mobiles utiles */}
            {/* <Card className="p-6 dark:bg-gray-800 dark:border-gray-700">
              <h3 className="text-xl font-semibold mb-6 dark:text-gray-300">Applications mobiles utiles</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  {
                    name: "KUCCPS Mobile",
                    description: "Application officielle pour les inscriptions universitaires",
                    category: "Éducation",
                  },
                  {
                    name: "HELB App",
                    description: "Gestion des prêts étudiants et bourses",
                    category: "Finance",
                  },
                  {
                    name: "M-Pesa",
                    description: "Paiements mobiles pour les frais universitaires",
                    category: "Finance",
                  },
                  {
                    name: "Uber/Bolt",
                    description: "Transport pour se rendre aux universités",
                    category: "Transport",
                  },
                  {
                    name: "Jumia",
                    description: "Achats en ligne pour les fournitures étudiantes",
                    category: "Shopping",
                  },
                  {
                    name: "Google Translate",
                    description: "Traduction pour les documents en langues locales",
                    category: "Utilitaires",
                  },
                ].map((app, index) => (
                  <div key={index} className="border dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium dark:text-gray-300">{app.name}</h4>
                      <Badge variant="secondary">{app.category}</Badge>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{app.description}</p>
                  </div>
                ))}
              </div>
            </Card> */}
          </div>
        )}
      </div>

   
    </div>
  )
}

export default RegistProcedure
